import React, { useCallback, useRef, useState } from 'react';
import { MultiPageVideoModel, defaultSubtitlesElementParams, SubtitlesElement } from '@bynder-studio/render-core';
import { Button, Form } from '@bynder/design-system';
import { parseSrt } from './parseSrt';
import { FormSectionContent } from '../../index.styled';

const SubtitlesInput = ({
    creativeModel,
    frameRate,
    disabled,
}: {
    creativeModel: MultiPageVideoModel;
    frameRate: number;
    disabled?: boolean;
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string | null>(null);

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];

            if (!file) {
                return;
            }

            const reader = new FileReader();

            reader.onload = (e) => {
                const content = e.target?.result as string;
                const cues = parseSrt(content, frameRate);

                // Creating it here because we don't want it to create any bugs while POC.
                creativeModel.setSubtitlesElement(new SubtitlesElement({ ...defaultSubtitlesElementParams }));
                creativeModel.updateGlobalProperty('SUBTITLES', { cues, enabled: true });
                setFileName(file.name);
            };

            reader.readAsText(file);
        },
        [creativeModel, frameRate],
    );

    const handleRemove = useCallback(() => {
        creativeModel.updateGlobalProperty('SUBTITLES', { cues: [], enabled: false });
        setFileName(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [creativeModel]);

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    return (
        <FormSectionContent onSubmit={(event) => event.preventDefault()}>
            <Form.Group>
                <Form.Label htmlFor="global_subtitles">Subtitles</Form.Label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".srt,application/x-subrip,text/plain"
                    onChange={handleFileChange}
                    disabled={disabled}
                    style={{ display: 'none' }}
                    id="global_subtitles_file"
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button variant="secondary" onClick={handleUploadClick} isDisabled={disabled}>
                        {fileName ? 'Replace SRT' : 'Upload SRT'}
                    </Button>
                    {fileName && (
                        <Button variant="clean" onClick={handleRemove} isDisabled={disabled}>
                            Remove
                        </Button>
                    )}
                </div>
                {fileName && <Form.HelperText id="global_subtitles_message">{fileName}</Form.HelperText>}
            </Form.Group>
        </FormSectionContent>
    );
};

export default SubtitlesInput;
